import { ComponentPropsWithoutRef } from "react";
import { useTranslation } from "react-i18next";
import { Controller, Control, FieldValues, Path } from "react-hook-form";
import { Select, SelectItem, TextInput } from "@carbon/react";
import {
  countryCodes,
  formatPhoneWithCountryCode,
  parsePhoneNumber,
} from "../../../utils";
import "./phoneField.scss";

type CarbonTextInputProps = ComponentPropsWithoutRef<typeof TextInput>;

export type PhoneFieldProps<TFieldValues extends FieldValues> = Omit<
  CarbonTextInputProps,
  | "id"
  | "labelText"
  | "invalid"
  | "invalidText"
  | "name"
  | "onChange"
  | "onBlur"
  | "value"
  | "defaultValue"
  | "ref"
  | "type"
> & {
  /** The RHF field name — also becomes the phone-number input's `id`, the
   * same convention `TextField`/`SelectField` use. The stored value is a
   * single formatted string ("+54 1145678900"), the same shape
   * `PhoneInput` (the pre-Carbon widget this replaces) produced. */
  name: Path<TFieldValues>;
  /** The phone-number input's visible label. */
  label: string;
  /** The country-code select's accessible name. Rendered visually hidden
   * (Carbon's `hideLabel`) — the dial code sits right next to the number
   * it prefixes and doesn't need a second visible caption, but it still
   * needs its own name for `getByRole("combobox", { name })` and a screen
   * reader, exactly like the hidden label on a search box's icon button. */
  countryCodeLabel: string;
  control: Control<TFieldValues>;
  /** Appends the translated `form.optional` marker ("(optional)") to `label` (the digits
   * input's label only — the dial-code select's label is already hidden). Same convention and
   * same rationale as `TextField`'s `optional` — see that component's docstring for Carbon's
   * full required/optional-marking guidance. */
  optional?: boolean;
};

/**
 * A country-code-aware phone field bound to a single React Hook Form value via `Controller` —
 * the `PhoneField` Task 7's review flagged as missing: `phone` doesn't fit `TextField` because
 * it's really two inputs (a dial-code `Select` and a digits `TextInput`) that must still resolve
 * to one Yup-validated string.
 *
 * Deliberately holds no local state of its own. Both the old `PhoneInput` and `EditUserForm`'s
 * usage of it kept the dial code and the digits in component state, re-deriving them from the
 * incoming `value` only on mount/change via a `useEffect` — a second source of truth that can
 * drift from what RHF actually holds. Here the single RHF value (`field.value`, a formatted
 * string like "+54 1145678900") is parsed fresh on every render via `parsePhoneNumber` and
 * written back through `formatPhoneWithCountryCode` on every keystroke — the field is fully
 * controlled by RHF, the same as `TextField`/`SelectField`.
 *
 * Accessibility guarantee this primitive exists to make cheap, same contract as `TextField`: the
 * digits input carries a real `<label htmlFor>` (`getByLabelText(label)` resolves to it) and, on
 * an invalid field, `aria-invalid` plus Carbon's `aria-errormessage` pointing at the rendered
 * `invalidText` — the error is announced against the phone field specifically, not read out
 * loose. The dial-code `Select` is reachable too (`getByRole("combobox", { name:
 * countryCodeLabel })`), just with its label visually hidden rather than absent.
 */
function PhoneField<TFieldValues extends FieldValues>({
  name,
  label,
  countryCodeLabel,
  control,
  required,
  optional,
  ...rest
}: PhoneFieldProps<TFieldValues>) {
  const { t } = useTranslation();
  const labelText = optional ? `${label} ${t("form.optional")}` : label;

  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState }) => {
        const { dialCode: parsedDialCode, phoneNumber } = parsePhoneNumber(
          field.value ?? ""
        );
        // Mirrors the old PhoneInput's own default: nothing chosen yet reads as +1 rather than
        // a blank, unusable select.
        const dialCode = parsedDialCode || "+1";

        const handleDialCodeChange = (
          event: React.ChangeEvent<HTMLSelectElement>
        ) => {
          field.onChange(
            formatPhoneWithCountryCode(phoneNumber, event.target.value)
          );
        };

        const handlePhoneNumberChange = (
          event: React.ChangeEvent<HTMLInputElement>
        ) => {
          field.onChange(
            formatPhoneWithCountryCode(event.target.value, dialCode)
          );
        };

        return (
          <div className="phone-field">
            <Select
              id={`${name}-country-code`}
              labelText={countryCodeLabel}
              hideLabel
              className="phone-field__country-code"
              value={dialCode}
              onChange={handleDialCodeChange}
              onBlur={field.onBlur}
            >
              {countryCodes.map((country) => (
                <SelectItem
                  key={country.code}
                  value={country.dialCode}
                  text={`${country.name} (${country.dialCode})`}
                />
              ))}
            </Select>
            <TextInput
              {...rest}
              id={name}
              type="tel"
              labelText={labelText}
              className="phone-field__number"
              value={phoneNumber}
              required={required}
              onChange={handlePhoneNumberChange}
              onBlur={field.onBlur}
              invalid={Boolean(fieldState.error)}
              invalidText={fieldState.error?.message}
            />
          </div>
        );
      }}
    />
  );
}

export default PhoneField;
